import { useEffect, useMemo } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu as MenuIcon } from 'lucide-react';
import { NAV, visibleNavSections, type NavEntry } from '@/Pages/nav';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BrandLockup } from '@/components/BrandMark';
import { SyncBanner } from '@/components/SyncBanner';
import { NotificationBell } from '@/Pages/Platform/NotificationBell';
import { usePermission } from '@/Hooks/usePermission';
import { useAuth } from '@/Hooks/useAuth';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { logout } from '@/Redux/Slices/authSlice';
import { closeMobileNav, setActiveBranch, toggleMobileNav, toggleSidebar } from '@/Redux/Slices/uiSlice';


export default function Layout() {
  const { t } = useTranslation();
  const { canAny } = usePermission();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector((s) => s.ui.sidebarOpen);
  const mobileNavOpen = useAppSelector((s) => s.ui.mobileNavOpen);

  // Overview carries no `perms` at all — everyone lands here — so it is
  // pinned above every section rather than filtered into one; grouping the
  // one row nobody can lose would be grouping by nothing.
  const home = NAV.find((item) => item.to === '/');
  const sections = visibleNavSections(canAny);

  return (
    <div className="flex h-dvh overflow-hidden">
      {/* Below `md` the rail is an overlay, so it needs something to dismiss it
          that is not the hamburger it is covering. */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => dispatch(closeMobileNav())}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'flex-col border-e bg-surface',
          /* Phone and small tablet: off-canvas. It used to be `shrink-0 w-64`
             at every width, which on a 320pt phone left 64pt of page for the
             entire dashboard — the rail was collapsible but never actually
             left the flow. */
          'fixed inset-y-0 start-0 z-40 w-64',
          /* Desktop: back in the flow, and the collapse becomes a width again. */
          'md:static md:z-auto md:shrink-0 md:transition-[width] md:duration-200',
          sidebarOpen ? 'md:w-64' : 'md:w-16',
          mobileNavOpen ? 'flex' : 'hidden md:flex',
        )}
      >
        {/* The product introduces itself here and on the login card, and nowhere
            else. The mark is `tarabeezaMark` from `@/lib/ui-tokens` — one
            definition for seven clients — and it follows the resolved theme, so
            the cream-tile variant lands on the light ground. Collapsed, the
            wordmark goes and the mark stays: it is the thing that identifies the
            rail at 64px. */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center border-b px-4',
            // Only the desktop rail ever collapses; the drawer is always 256pt
            // wide, so the wordmark and the padding stay there.
            !sidebarOpen && 'md:justify-center md:px-0',
          )}
        >
          <BrandLockup size={32} nameClassName={sidebarOpen ? undefined : 'md:hidden'} />
        </div>

        <nav className="scrollbar-thin flex-1 overflow-y-auto p-2" aria-label={t('common.menu')}>
          {home && (
            <div className="space-y-1">
              <NavRow item={home} sidebarOpen={sidebarOpen} onNavigate={() => dispatch(closeMobileNav())} />
            </div>
          )}

          {/*
              Each section is `role="group"` with its own `aria-label`, so its
              name reaches a screen reader whether or not the header text
              below is on screen — and each carries a plain `title`, so the
              same name is available on hover even collapsed to 64px, where
              the header is hidden and the `border-t` above is the only other
              thing marking a new group. Nothing here collapses or expands on
              its own: every row stays in the DOM and in the Tab order all the
              time, so there is no section state to trap focus or to announce.
          */}
          {sections.map((section) => (
            <div
              key={section.key}
              role="group"
              aria-label={t(section.key)}
              title={t(section.key)}
              className="mt-2 space-y-1 border-t pt-2"
            >
              <p
                className={cn(
                  'truncate px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted',
                  !sidebarOpen && 'md:hidden',
                )}
                aria-hidden
              >
                {t(section.key)}
              </p>
              {section.items.map((item) => (
                <NavRow
                  key={item.to}
                  item={item}
                  sidebarOpen={sidebarOpen}
                  onNavigate={() => dispatch(closeMobileNav())}
                />
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* `min-h-16` + `flex-wrap`, not `h-16`: the control cluster is wider
            than a 320pt viewport on its own, and a fixed-height row could only
            clip it. Wrapping costs a second row on a phone and nothing at all
            from `sm` up. */}
        <header className="flex min-h-16 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b bg-surface px-4 py-2">
          {/* Two triggers rather than one that branches on a measured width:
              each is scoped to the breakpoint whose state it owns. */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={t('common.menu')}
            aria-expanded={mobileNavOpen}
            onClick={() => dispatch(toggleMobileNav())}
          >
            <MenuIcon className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:inline-flex"
            aria-label={t('common.menu')}
            aria-expanded={sidebarOpen}
            onClick={() => dispatch(toggleSidebar())}
          >
            <MenuIcon className="h-5 w-5" />
          </Button>

          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            <BranchSwitcher />
            <NotificationBell />
            <ThemeToggle />
            <LanguageSwitcher />
            <span className="hidden text-sm text-ink-muted sm:inline">
              {user?.firstName} {user?.lastName}
            </span>
            <Button variant="ghost" size="icon" onClick={() => void dispatch(logout())}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>

        {/* Under the header, above every page: queued and rejected work is not a
            per-screen concern, and it must stay visible while the operator
            navigates away from wherever they created it. Silent when there is
            nothing to say. */}
        <SyncBanner />

        <main className="scrollbar-thin flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * One rail row, shared by the pinned Overview link and every section below
 * it. Unchanged from before the rail grew sections: same classes, same
 * active-state styling, same collapsed-label behaviour — only pulled out so
 * it is not written out twice.
 */
function NavRow({
  item: { to, key, icon: glyph, defaultValue },
  sidebarOpen,
  onNavigate,
}: {
  item: NavEntry;
  sidebarOpen: boolean;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
          isActive ? 'bg-accent-soft font-medium text-accent-soft-fg' : 'text-ink-muted hover:bg-hover',
        )
      }
    >
      <Icon as={glyph} size="lg" className="shrink-0" />
      <span className={cn('truncate', !sidebarOpen && 'md:hidden')}>{t(key, { defaultValue })}</span>
    </NavLink>
  );
}

interface BranchRow {
  id: string;
  name: string;
}

/**
 * D-06 — the header branch switcher.
 *
 * `setActiveBranch` existed and was dispatched from nowhere, so `activeBranchId`
 * was permanently null and every bare `useCurrency()` fell through to the
 * *tenant's* currency and decimals: a KWD branch's PS5 session total rendered as
 * `SAR 12.35` instead of `KWD 12.345`. This is the missing writer. The choice
 * persists through the slice (`localStorage['venue.branch']`), so it survives a
 * reload and the currency cache resolves the same branch on the next page load.
 *
 * Gated on `branches.view`, which is what `GET /branches` actually requires —
 * `settings.view` is the D-05 mistake and no operational role holds it.
 *
 * "All branches" is a real choice, not a blank: it resolves money to the tenant
 * base currency, which is correct for a single-currency chain and is why the
 * cross-branch screens (Overview, Reports) label such totals explicitly rather
 * than borrowing a branch's code.
 */
function BranchSwitcher() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const dispatch = useAppDispatch();
  const activeBranchId = useAppSelector((s) => s.ui.activeBranchId);

  const allowed = can('branches.view');
  const { data } = useApi<unknown>(allowed ? '/branches' : null, { limit: 200 });
  const branches = useMemo(() => rowsOf<BranchRow>(data).rows, [data]);

  useEffect(() => {
    if (!branches.length) return;
    // A persisted id the actor can no longer see is worse than none: it would
    // keep pricing screens in a branch they have lost access to.
    if (activeBranchId && !branches.some((branch) => branch.id === activeBranchId)) {
      dispatch(setActiveBranch(null));
      return;
    }
    // With exactly one branch there is nothing to choose, and leaving it null
    // would resolve money against the tenant rather than that branch.
    if (!activeBranchId && branches.length === 1) dispatch(setActiveBranch(branches[0].id));
  }, [branches, activeBranchId, dispatch]);

  if (!allowed || branches.length < 2) return null;

  return (
    <Select
      className="h-9 w-36 sm:w-44"
      value={activeBranchId ?? ''}
      aria-label={t('nav.branches')}
      onChange={(event) => dispatch(setActiveBranch(event.target.value || null))}
    >
      <option value="">{t('nav.allBranches')}</option>
      {branches.map((branch) => (
        <option key={branch.id} value={branch.id}>
          {branch.name}
        </option>
      ))}
    </Select>
  );
}
